import './styles.css'

import {
  CloseButton,
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react'
import { memo, useEffect, useRef, useState } from 'react'

import { CloseIcon, MinimizeIcon } from '../../../components/Icons'
import { useFetchPersonaById } from '../../api/apiHooks'
import { FilterOptions, Profile } from '../../api/types'
import { States } from './constants'
import { FailGenerationView } from './FailGenerationView'
import { GenerateMentorForm } from './GenerateMentorForm'
import { GenerateMentorIndicator } from './GenerateMentorIndicator'
import { GeneratingMentorView } from './GeneratingMentorView'
import { SaveMentorForm } from './SaveMentorForm'

interface ICreateMentorProps {
  isOpen: boolean
  closePopup: () => void
  openPopup: () => void
  minimizePopup: () => void
  filtersData?: FilterOptions
}

const pingingTime = 60 * 60 * 1000 // 1 hour

const GenerateMentor: React.FC<ICreateMentorProps> = memo(
  ({ isOpen, closePopup, openPopup, minimizePopup, filtersData }) => {
    const [isPinging, setIsPinging] = useState(false)
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const [status, setStatus] = useState<States>('default')
    const [generatingMentorId, setGeneratingMentorId] = useState('')
    const [generatedMentor, setGeneratedMentor] = useState<Profile | null>(null)
    const [mentorIsReady, setMentorIsReady] = useState(false)
    const [generatingError, setGeneratingError] = useState(false)

    const startTimeout = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      setIsPinging(true)
      timeoutRef.current = setTimeout(() => {
        setIsPinging(false)
        setGeneratingError(true)
        setStatus('error')
      }, pingingTime)
    }

    const stopTimeout = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
      setIsPinging(false)
    }

    const handleGenerateMentor = (mentorId: string) => {
      setGeneratingMentorId(mentorId)
      setStatus('generating')
      setMentorIsReady(false)
      startTimeout()
    }

    const { data, isSuccess, error, isError } = useFetchPersonaById(
      generatingMentorId,
      !mentorIsReady && !generatingError && isPinging,
    )

    const mentorStatus = data?.data?.profile?.profile?.status

    useEffect(() => {
      if (isSuccess) {
        setGeneratedMentor(data.data.profile.profile)
        setStatus('generated')
        if (mentorStatus === 'WaitingForApproval') {
          stopTimeout()
          setMentorIsReady(true)
        }
      }
    }, [mentorStatus, isSuccess, data])

    useEffect(() => {
      if (isError && error?.response?.status !== 404) {
        setStatus('error')
        setGeneratingError(true)
        stopTimeout()
      }
    }, [error, isError])

    const renderByStatus = {
      default: (
        <GenerateMentorForm
          onGenerate={handleGenerateMentor}
          setStatus={setStatus}
          closePopup={closePopup}
          filtersData={filtersData}
        />
      ),
      generating: <GeneratingMentorView />,
      generated: (
        <SaveMentorForm
          mentorIsReady={mentorIsReady}
          generatedMentor={generatedMentor}
          filtersData={filtersData}
          closePopup={closePopup}
        />
      ),
      error: <FailGenerationView closePopup={closePopup} />,
    }

    return (
      <>
        <Dialog
          className="relative z-50"
          unmount={false}
          open={isOpen}
          onClose={minimizePopup}
        >
          <DialogBackdrop className="tc-mv-backdrop" />
          <div className="fixed inset-0 overflow-y-auto h-screen flex items-center justify-center">
            <DialogPanel className="tc-mv-popup mx-auto">
              <DialogTitle className="text-lg text-white w-full flex py-4 px-6 border-b border-white/10">
                Generate companion
                {status === 'error' ? (
                  <CloseButton
                    onClick={closePopup}
                    className="text-white ms-auto opacity-70 hover:opacity-100"
                  >
                    <CloseIcon />
                  </CloseButton>
                ) : (
                  <CloseButton
                    onClick={minimizePopup}
                    className="text-white ms-auto opacity-70 hover:opacity-100"
                  >
                    <MinimizeIcon />
                  </CloseButton>
                )}
              </DialogTitle>
              <div>{renderByStatus[status]}</div>
            </DialogPanel>
          </div>
        </Dialog>
        {!isOpen && (
          <GenerateMentorIndicator
            status={
              status === 'generated' && !mentorIsReady ? 'generating' : status
            }
            openPopup={openPopup}
          />
        )}
      </>
    )
  },
)

export { GenerateMentor }
